'use client';

import {
  ChooseButtonGroup,
  LowStockIcon,
  RingFinishChoices,
  type RingFinishData,
  Tag,
  TypographyV3,
} from '@jouzen/ecom-components';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';

import { EventType } from '@/analytics/types';
import type { RingFinish } from '@/app/types/ring';

import { FinishStepRingFinishChoices } from './FinishStepRingFinishChoices';
import useFinishStep from './hooks/useFinishStep';

const FinishStep = (): JSX.Element => {
  const t = useTranslations();
  const path = usePathname();

  const {
    currencyCode,
    hasMaxRingCount,
    isOuraRing4Wizard,
    isSizesHalfDepleted,
    or4RingFinishesData,
    renderFinishes,
    ringCount,
    selectedFinish,
    onSelect,
    ...rest
  } = useFinishStep();

  const handleAnalytics = async () => {
    await window.ouraAnalytics.track(EventType.LinkClicked, {
      cta: 'Limited sizes available. Shop the newest Oura Ring 4 here.',
      action: 'Goes to PDP Wiz Gen 4',
      location: 'finish',
      path,
    });
  };

  const label: ReactNode = hasMaxRingCount
    ? t('cart_is_full')
    : t('pdp_finish_title');
  const description: ReactNode | null = hasMaxRingCount
    ? t.rich('cart_full_ring_limit', { amount: ringCount })
    : null;

  return (
    <>
      {!isOuraRing4Wizard && (
        <div className="mb-4">
          <Tag variant="blue" size="small">
            {t('gen3_special_pricing')}
          </Tag>
        </div>
      )}
      {isOuraRing4Wizard ? (
        <RingFinishChoices
          currencyCode={currencyCode}
          description={description}
          disabled={hasMaxRingCount}
          initialSelectedFinish={selectedFinish as string}
          label={label}
          ringFinishesData={or4RingFinishesData as RingFinishData[]}
          onChange={(finish) => onSelect(finish as RingFinish)}
        />
      ) : (
        // Delete FinishStepRingFinishChoices in favor of RingFinishChoices when Gen3 sells out
        <ChooseButtonGroup
          description={description}
          label={label}
          value={selectedFinish!}
          onChange={(finish) => onSelect(finish as RingFinish)}
        >
          <FinishStepRingFinishChoices
            hasMaxRingCount={hasMaxRingCount}
            isOuraRing4Wizard={isOuraRing4Wizard}
            renderFinishes={renderFinishes}
            ringCount={ringCount}
            selectedFinish={selectedFinish}
            {...rest}
          />
        </ChooseButtonGroup>
      )}
      {isSizesHalfDepleted && (
        <TypographyV3 className="mt-4 flex items-center gap-1 text-red-600">
          <LowStockIcon className="inline size-4" />
          <span>
            {t.rich('inventory_insights_finish_step_low_stock_msg', {
              link: function link(chunks: ReactNode) {
                return (
                  <Link
                    href="/product/rings/oura-ring-4/silver/"
                    className="text-blue-100 underline"
                    onClick={handleAnalytics}
                  >
                    {chunks}
                  </Link>
                );
              },
            })}
          </span>
        </TypographyV3>
      )}
    </>
  );
};

export default FinishStep;
